import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import PageHero from "@/components/PageHero";
import { Shield, BookOpen, Target, HeartPulse, Users, Ban } from "lucide-react";

const commitments = [
  {
    icon: Shield,
    title: "Säker miljö",
    text: "Värna om och respektera vår miljö, både när det gäller inre och yttre miljö på företaget.",
  },
  {
    icon: BookOpen,
    title: "Lagar och föreskrifter",
    text: "Fastställa rutiner så att vi alltid följer de arbetsmiljölagar och andra föreskrifter som gäller för vår verksamhet.",
  },
  {
    icon: Users,
    title: "Utbildning och engagemang",
    text: "Genom utbildning och information engagera våra medarbetare i hälso- och arbetsmiljöfrågor.",
  },
  {
    icon: Target,
    title: "Noll skador",
    text: "Vi har som mål att ingen av våra medarbetare ska drabbas av skada eller annan ohälsa vid arbete hos företaget.",
  },
  {
    icon: HeartPulse,
    title: "Systematiskt arbetsmiljöarbete",
    text: "Vi arbetar systematiskt med att minimera riskerna för arbetsskador, olycksfall och tillbud samt med planerade aktiviteter främja medarbetarnas hälsa och arbetsglädje.",
  },
];

export default function Arbetsmiljopolicy() {
  return (
    <Layout>
      <SEOHead
        title="Arbetsmiljöpolicy | ASPAB"
        description="Läs om ASPAB:s arbetsmiljöpolicy. Vi skapar en fysisk, psykisk och socialt sund arbetsplats med mål om noll skador och systematiskt arbetsmiljöarbete."
        canonical="https://aspab.com/arbetsmiljopolicy"
        path="/arbetsmiljopolicy"
        breadcrumbs={[
          { name: "Policyer och villkor", href: "/policyer" },
          { name: "Arbetsmiljöpolicy", href: "/arbetsmiljopolicy" },
        ]}

        ogImage="/images/og-arbetsmiljopolicy_c7295b2d.png"
      />

      <PageHero
        title="Arbetsmiljöpolicy"
        subtitle="En god och säker arbetsmiljö är en viktig fråga för Aspab AB."
        breadcrumbs={[
          { name: "Policyer och villkor", href: "/policyer" },
          { name: "Arbetsmiljöpolicy", href: "/arbetsmiljopolicy" },
        ]}
      />

      <section className="bg-white py-20 md:py-28">
        <div className="container">
          <div className="max-w-3xl mx-auto">

            <div className="space-y-12">

              {/* Inledning */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900">
                  Vår målsättning
                </h2>
                <p className="text-gray-600 mt-4 leading-relaxed">
                  En god och säker arbetsmiljö är en viktig fråga för Aspab AB. Målsättningen med
                  vårt arbetsmiljöarbete är att skapa en fysisk, psykisk och socialt sund och
                  utvecklande arbetsplats för alla medarbetare.
                </p>
              </div>

              {/* Åtaganden */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900">
                  Vi uppnår detta genom att
                </h2>
                <div className="mt-6 space-y-4">
                  {commitments.map((item, i) => (
                    <div key={i} className="flex gap-4 p-5 bg-gray-50 border border-gray-100 rounded-lg">
                      <div className="w-10 h-10 shrink-0 flex items-center justify-center bg-[#2563EB]/10 rounded-lg">
                        <item.icon className="w-5 h-5 text-[#2563EB]" />
                      </div>
                      <div className="self-center">
                        <h3 className="font-heading text-base font-semibold text-gray-900">{item.title}</h3>
                        <p className="text-gray-600 text-sm mt-1 leading-relaxed">{item.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Personligt ansvar */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900">
                  Personligt ansvar
                </h2>
                <div className="flex gap-4 mt-4 p-5 bg-blue-50 border border-blue-100 rounded-lg">
                  <div className="w-10 h-10 shrink-0 flex items-center justify-center bg-[#2563EB]/10 rounded-lg">
                    <Users className="w-5 h-5 text-[#2563EB]" />
                  </div>
                  <p className="text-gray-600 leading-relaxed self-center">
                    Samtliga medarbetare ska känna ett personligt ansvar för hälsa och arbetsmiljö
                    i det dagliga arbetet.
                  </p>
                </div>
              </div>

              {/* Diskriminering */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900">
                  Nolltolerans mot diskriminering
                </h2>
                <div className="flex gap-4 mt-4 p-5 bg-gray-50 border border-gray-100 rounded-lg">
                  <div className="w-10 h-10 shrink-0 flex items-center justify-center bg-red-50 rounded-lg">
                    <Ban className="w-5 h-5 text-red-500" />
                  </div>
                  <p className="text-gray-600 leading-relaxed self-center">
                    Alla medarbetare ska förstå och sträva efter att alla former av diskriminering
                    inte accepteras på vår arbetsplats.
                  </p>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
